import React, { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { Box, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

import { PRIMARY_DARK_ADMIN, sectionAccentColor } from '../../../shared/ui/editorAccents';
import paletteStyles from '../palette/palette.module.css';
import { useInspectorSectionState } from './useInspectorSectionState';
import styles from './puckSectionField.module.css';

const PUCK_FIELD_WRAPPER = '[class*="PuckFields-field"]';
const HEADING_MARKER = '[data-puck-section-heading]';

/**
 * Masque les réglages d'une section repliée : ses champs sont les wrappers
 * Puck qui suivent le titre, jusqu'au titre suivant. Puck n'a ni groupe de
 * champs ni nom de champ dans le DOM, et ne relit `visible` qu'au
 * `resolveFields` : on pose donc un attribut sur ces wrappers, que le CSS de
 * l'inspecteur cache (SECTION_COLLAPSE_CSS). L'observateur réapplique le
 * masquage quand `resolveFields` ajoute ou retire un champ.
 */
function useCollapseFollowingFields(headingRef, collapsed) {
  useLayoutEffect(() => {
    const wrapper = headingRef.current?.closest(PUCK_FIELD_WRAPPER);
    const list = wrapper?.parentElement;
    if (!wrapper || !list) return undefined;

    const markFollowing = (hidden) => {
      let next = wrapper.nextElementSibling;
      while (next && !next.querySelector(HEADING_MARKER)) {
        next.toggleAttribute('data-section-collapsed', hidden);
        next = next.nextElementSibling;
      }
    };

    markFollowing(collapsed);
    const observer = new MutationObserver(() => markFollowing(collapsed));
    observer.observe(list, { childList: true });
    return () => {
      observer.disconnect();
      markFollowing(false);
    };
  }, [headingRef, collapsed]);
}

export function HeadingLabel({ label, color }) {
  return (
    <Text
      component="span"
      display="inline-block"
      fz={12}
      fw={700}
      c={color}
      style={{
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        borderBottom: `2px solid ${color}`,
        paddingBottom: 4,
      }}
    >
      {label}
    </Text>
  );
}

function CollapsibleHeading({
  label, color, collapsedByDefault, hidesFollowingFields,
}) {
  const ref = useRef(null);
  const [collapsed, toggle] = useInspectorSectionState(label, collapsedByDefault);
  useCollapseFollowingFields(ref, hidesFollowingFields && collapsed);

  return (
    <Box ref={ref} data-puck-section-heading="">
      <button type="button" className={styles.toggle} onClick={toggle} aria-expanded={!collapsed}>
        <HeadingLabel label={label} color={color} />
        <FontAwesomeIcon
          icon={icon({ name: 'chevron-down', style: 'solid' })}
          style={{ fontSize: 10, color }}
          className={`${paletteStyles.chevron} ${collapsed ? paletteStyles.chevronCollapsed : ''}`}
        />
      </button>
    </Box>
  );
}

/**
 * Un titre de section dans l'inspecteur, à la manière de l'éditeur classique :
 * libellé en majuscules souligné de sa couleur, la même des deux côtés
 * (`shared/ui/editorAccents`). Un clic replie la section, pour toutes les box
 * qui ont la même (cf. useInspectorSectionState) ; le classique ne replie pas
 * ses sections, le chevron est l'ajout du web.
 *
 * Puck 0.21 n'a pas de séparateur de champs — `overrides.fields` enveloppe la
 * liste entière et ses `children` sont opaques. Mais `overrides.fieldTypes`
 * accepte les types inventés (c'est déjà ainsi qu'`iconPicker` est branché) :
 * une entrée de `fields` du type `section` rend ce titre et **n'écrit aucune
 * prop** — elle ne touche jamais à `onChange`.
 *
 * D'où la clé conventionnelle `_section*` dans `fields`, et l'interdiction de
 * la déclarer dans `defaultProps` : rien ne doit atterrir dans les données.
 *
 * `collapsible={false}` : titre fixe (« Actions », toujours ouvert).
 * `ownContent` : la section porte ses réglages dans son propre champ (Texte) —
 * la replier ne masque pas les champs Puck qui suivent, c'est à elle de
 * cacher son contenu (useInspectorSectionState).
 */
export function PuckSectionHeading({
  label, accent, field, collapsible = true, ownContent = false,
}) {
  const color = accent ?? field?.accent ?? sectionAccentColor(label);
  if (collapsible) {
    return (
      <CollapsibleHeading
        label={label}
        color={color}
        collapsedByDefault={Boolean(field?.collapsedByDefault)}
        hidesFollowingFields={!ownContent}
      />
    );
  }
  return (
    /* Le marqueur sert au correctif CSS (`puckUiFixCss`) : Puck sépare CHAQUE
       champ d'un filet, ce qui hachait un groupe en autant de tranches que de
       réglages. Le filet ne subsiste qu'ici, devant le titre. */
    <Box data-puck-section-heading="">
      <HeadingLabel label={label} color={color} />
    </Box>
  );
}

/**
 * Déclare un titre de section entre deux champs d'un bloc.
 * `collapsedByDefault` : repliée tant que l'utilisateur ne l'a pas ouverte
 * (Réglages avancés).
 */
export const puckSectionField = (label, { collapsedByDefault = false, accent } = {}) => ({
  type: 'section',
  label,
  ...(collapsedByDefault ? { collapsedByDefault } : {}),
  ...(accent ? { accent } : {}),
});

/**
 * Section de la galerie de styles d'un bloc, titrée par son nom. Même teinte
 * pour tous les blocs, hors de l'échelle des accents : jamais celle de
 * « Style » qui la suit, ni d'aucune voisine — le nom du bloc vit aussi dans
 * le registre partagé avec le classique, d'où la couleur posée ici.
 */
export const puckStyleGallerySectionField = (label) => puckSectionField(label, { accent: PRIMARY_DARK_ADMIN });

const ADVANCED_SECTION_TITLE = 'Réglages avancés';

/**
 * « Réglages avancés », en dernier : les réglages rares d'une box, repliés
 * tant qu'on ne les ouvre pas. Une box sans réglage rare n'en a pas.
 */
export const puckAdvancedSectionField = () => puckSectionField(ADVANCED_SECTION_TITLE, { collapsedByDefault: true });

/**
 * Vrai là où les réglages avancés s'affichent sans leur titre : le panneau
 * Thème, qui ne garde d'un tableau que ces réglages-là.
 */
export const AdvancedSectionExpandedContext = createContext(false);

/**
 * La section est-elle ouverte ? Repliée, elle ne fait que masquer ses champs :
 * un champ coûteux (une vingtaine de contrôles redessinés à chaque frappe) s'en
 * sert pour ne rien monter tant qu'on ne l'ouvre pas.
 */
export function useAdvancedSectionOpen() {
  const expanded = useContext(AdvancedSectionExpandedContext);
  const [collapsed] = useInspectorSectionState(ADVANCED_SECTION_TITLE, true);
  return expanded || !collapsed;
}

/** Un champ est-il un titre de section ? */
export const isSectionField = (field) => field?.type === 'section';

/**
 * Retire les titres dont le groupe s'est vidé.
 *
 * `resolveFields` filtre des champs selon le contexte (un Bloc posé dans un
 * onglet perd ceux qui n'ont de sens qu'au contact de la page) : sans ce
 * nettoyage, son titre resterait seul, à annoncer des réglages absents.
 */
export function dropEmptySections(fields) {
  const entries = Object.entries(fields || {});
  const kept = entries.filter(([, field], index) => {
    if (!isSectionField(field)) return true;
    const next = entries.slice(index + 1).find(([, f]) => !isSectionField(f) || true);
    return !!next && !isSectionField(next[1]);
  });
  return Object.fromEntries(kept);
}

export default puckSectionField;
